import React from 'react'
import { Box, Typography, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Lawsuit } from '../LawsuitsBusinessCheck/types'
import { LawsuitsOwnersCheckDetails } from './types'
import CollapsibleSection from '../CollapsableSection'
import StatItem from '../StatItem'
import { formatDate, ObjectDetails } from '../ObjectDetails'

interface Props {
  data: LawsuitsOwnersCheckDetails
  theme: any
}

const capitalizeFirst = (str: string = ''): string => {
  if (!str) return ''
  const [first, ...rest] = str
  return first.toUpperCase() + rest.join('').toLowerCase()
}

const LawsuitContent: React.FC<{ lawsuit: Lawsuit }> = ({ lawsuit }) => {
  const { t } = useTranslation(['common'])

  const simpleDetails = {
    Number: lawsuit.Number,
    Type: lawsuit.Type,
    MainSubject: lawsuit.MainSubject,
    CourtName: lawsuit.CourtName,
    CourtLevel: lawsuit.CourtLevel,
    CourtType: lawsuit.CourtType,
    CourtDistrict: lawsuit.CourtDistrict,
    Judge: lawsuit.Judge,
    JudgingBody: lawsuit.JudgingBody,
    State: lawsuit.State,
    Status: lawsuit.Status,
    Value:
      lawsuit.Value === -1
        ? t('common:lawsuits.NotInformed')
        : lawsuit.Value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), // Handle -1 value
    LastUpdate: formatDate(lawsuit.LastUpdate),
    NumberOfParties: lawsuit.NumberOfParties,
    NumberOfUpdates: lawsuit.NumberOfUpdates,
    NumberOfVolumes: lawsuit.NumberOfVolumes,
    NumberOfPages: lawsuit.NumberOfPages,
    AverageNumberOfUpdatesPerMonth: lawsuit.AverageNumberOfUpdatesPerMonth,
    LawSuitAge: lawsuit.LawSuitAge,
  }

  return (
    <Box>
      <ObjectDetails data={simpleDetails} />
      {lawsuit.RelatedLawsuits && lawsuit.RelatedLawsuits.length > 0 && (
        <CollapsibleSection title={t('common:lawsuits.Procesos Relacionados')}>
          {lawsuit.RelatedLawsuits.map((related) => (
            <Typography key={related}>{related}</Typography>
          ))}
        </CollapsibleSection>
      )}
      {lawsuit.OtherSubjects && lawsuit.OtherSubjects.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.OtherSubjectsTitle')} (${lawsuit.OtherSubjects.length})`}>
          {lawsuit.OtherSubjects.map((subject) => (
            <Typography
              key={subject}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={subject}
            >
              {subject}
            </Typography>
          ))}
        </CollapsibleSection>
      )}
      {lawsuit.Parties && lawsuit.Parties.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.PartiesTitle')} (${lawsuit.Parties.length})`}>
          {lawsuit.Parties?.map((party) => (
            <CollapsibleSection key={party.Doc || party.Name} title={`${t('common:lawsuits.Name')}: ${party.Name}`}>
              <ObjectDetails data={party} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}
      {lawsuit.Updates && lawsuit.Updates.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.UpdatesTitle')} (${lawsuit.Updates.length})`}>
          {lawsuit.Updates?.map((update, idx) => (
            <CollapsibleSection
              key={`${update.PublishDate}-${update.Content}-${idx}`}
              title={`${t('common:lawsuits.Published')}: ${formatDate(update.PublishDate)}`}
            >
              <ObjectDetails data={update} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}
      {lawsuit.Petitions && lawsuit.Petitions.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.PetitionsTitle')} (${lawsuit.Petitions.length})`}>
          {lawsuit.Petitions.map((petition, idx) => {
            const date = formatDate(petition.CreationDate)
            const text = petition.Author
              ? `${petition.Author} - ${petition.Type} - ${date}`
              : `${petition.Type} - ${date}`
            return <Typography key={idx}>{text}</Typography>
          })}
        </CollapsibleSection>
      )}
      {lawsuit.Decisions && lawsuit.Decisions.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.DecisionsTitle')} (${lawsuit.Decisions.length})`}>
          {lawsuit.Decisions?.map((decision, idx) => (
            <CollapsibleSection
              key={`${decision.DecisionDate}-${decision.DecisionContent}-${idx}`}
              title={`${t('common:lawsuits.Date')}: ${formatDate(decision.DecisionDate)}`}
            >
              <ObjectDetails data={decision} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}
    </Box>
  )
}

const LawsuitsOwnersCheck: React.FC<Props> = ({ data, theme }) => {
  const { t } = useTranslation(['common'])

  const ownerInfoMap = React.useMemo(() => {
    const map = new Map<string, string>()
    if (!data.Lawsuits) {
      return map
    }
    for (const ownerData of Object.values(data.Lawsuits)) {
      if (ownerData.Lawsuits) {
        for (const lawsuit of ownerData.Lawsuits) {
          if (lawsuit.Parties) {
            for (const party of lawsuit.Parties) {
              if (party.Name && party.Doc) {
                map.set(party.Doc, party.Name)
              }
            }
          }
        }
      }
    }
    return map
  }, [data.Lawsuits])

  return (
    <Grid
      container
      spacing={2}
      className="accordion-jap"
      margin={0}
      width="100%"
      sx={{ backgroundColor: theme === 'light' ? '#FCFFFD' : '#020D06', overflowY: 'auto' }}
    >
      <Grid item xs={12} md={6}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            {t('common:lawsuits.Totals')}
          </Typography>
          <Grid container spacing={1} sx={{ pl: 1 }}>
            <Grid item xs={12} sm={6}>
              <StatItem label={t('common:lawsuits.TotalLawsuits')} value={data.TotalLawsuits} />
              <StatItem label={t('common:lawsuits.TotalReplacedLawsuits')} value={data.TotalReplacedLawsuits} />
              <StatItem label={t('common:lawsuits.TotalLawsuitsAsAuthor')} value={data.TotalLawsuitsAsAuthor} />
              <StatItem label={t('common:lawsuits.TotalLawsuitsAsDefendant')} value={data.TotalLawsuitsAsDefendant} />
              <StatItem label={t('common:lawsuits.TotalLawsuitsAsOther')} value={data.TotalLawsuitsAsOther} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatItem label={t('common:lawsuits.LawsuitsWithAllOwners')} value={data.LawsuitsWithAllOwners} />
              <StatItem label={t('common:lawsuits.AverageLawsuitsPerOwner')} value={data.AverageLawsuitsPerOwner} />
              <StatItem label={t('common:lawsuits.OwnerMaxLawsuits')} value={data.OwnerMaxLawsuits} />
              <StatItem label={t('common:lawsuits.OwnerMinLawsuits')} value={data.OwnerMinLawsuits} />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            {t('common:lawsuits.QuantityByDays')}
          </Typography>
          <Grid container spacing={1} sx={{ pl: 1 }}>
            <Grid item xs={12} sm={6}>
              <StatItem label={t('common:lawsuits.Last30DaysLawsuits')} value={data.Last30DaysLawsuits} />
              <StatItem label={t('common:lawsuits.Last90DaysLawsuits')} value={data.Last90DaysLawsuits} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatItem label={t('common:lawsuits.Last180DaysLawsuits')} value={data.Last180DaysLawsuits} />
              <StatItem label={t('common:lawsuits.Last365DaysLawsuits')} value={data.Last365DaysLawsuits} />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {/* <Grid item xs={12} sm={6}>
            <CollapsibleSection title={`${t('common:lawsuits.ActiveOwners')} (${data.ActiveOwners.length})`}>
              {data.ActiveOwners?.map((ownerDoc) => {
                const ownerName = ownerInfoMap.get(ownerDoc)
                const displayText = ownerName ? `${ownerDoc} - ${ownerName}` : ownerDoc
                return <Typography key={ownerDoc}>{displayText}</Typography>
              })}
            </CollapsibleSection>
          </Grid> */}
          {/* <Grid item xs={12} sm={6}>
            <CollapsibleSection title={`${t('common:lawsuits.InactiveOwners')} (${data.InactiveOwners.length})`}>
              {data.InactiveOwners?.map((ownerDoc) => {
                const ownerName = ownerInfoMap.get(ownerDoc)
                const displayText = ownerName ? `${ownerDoc} - ${ownerName}` : ownerDoc
                return <Typography key={ownerDoc}>{displayText}</Typography>
              })}
            </CollapsibleSection>
          </Grid> */}
        </Grid>
        <CollapsibleSection
          title={`${t('common:lawsuits.LawsuitListOwners')} (${data.Lawsuits ? Object.keys(data.Lawsuits).length : 0})`}
        >
          {data.Lawsuits &&
            Object.entries(data.Lawsuits).map(([key, value]) => {
              let isOwnerInactive = false
              if (data.InactiveOwners && data.InactiveOwners.length > 0 && data.InactiveOwners.includes(key)) { 
                isOwnerInactive = true;
              }
              const ownerName = ownerInfoMap.get(key)
              const title = ownerName
                ? `${t('common:lawsuits.Owner')}: ${ownerName} - ${key} ${isOwnerInactive ? `(${t('common:lawsuits.InactiveOwner')})` : ''}`
                : `${t('common:lawsuits.Owner')}: ${key} ${isOwnerInactive ? `(${t('common:lawsuits.InactiveOwner')})` : ''}`
              return (
                <CollapsibleSection key={key} title={title}>
                  {value.Lawsuits && value.Lawsuits.length > 0 ? (
                    <>
                      <Grid container spacing={2} width={600}>
                        <Grid item width={250}>
                          <StatItem label={t('common:lawsuits.TotalLawsuits')} value={value.TotalLawsuits} />
                        </Grid>
                        <Grid item width={250}>
                          <StatItem
                            label={t('common:lawsuits.TotalLawsuitsAsAuthor')}
                            value={value.TotalLawsuitsAsAuthor}
                          />
                        </Grid>
                        <Grid item width={250}>
                          <StatItem
                            label={t('common:lawsuits.TotalLawsuitsAsDefendant')}
                            value={value.TotalLawsuitsAsDefendant}
                          />
                        </Grid>
                        <Grid item width={250}>
                          <StatItem
                            label={t('common:lawsuits.TotalLawsuitsAsOther')}
                            value={value.TotalLawsuitsAsOther}
                          />
                        </Grid>
                      </Grid>
                      {(() => {
                        const groupedByCourtType =
                          value.Lawsuits?.reduce((acc, lawsuit) => {
                            acc[lawsuit.CourtType] = acc[lawsuit.CourtType] || []
                            acc[lawsuit.CourtType].push(lawsuit)
                            return acc
                          }, {} as Record<string, Lawsuit[]>) || {}

                        return Object.entries(groupedByCourtType).map(([courtType, lawsuitsArr]) => {
                          const groupedByMainSubject = lawsuitsArr.reduce((acc, lawsuit) => {
                            const mainSubject = lawsuit.MainSubject || 'N/A'
                            acc[mainSubject] = acc[mainSubject] || []
                            acc[mainSubject].push(lawsuit)
                            return acc
                          }, {} as Record<string, Lawsuit[]>)

                          return (
                            <CollapsibleSection
                              key={courtType}
                              title={`${capitalizeFirst(courtType)} (${lawsuitsArr.length})`}
                            >
                              {Object.entries(groupedByMainSubject).map(([mainSubject, subjectLawsuits]) => (
                                <CollapsibleSection
                                  key={mainSubject}
                                  title={`${capitalizeFirst(mainSubject)} (${subjectLawsuits.length})`}
                                >
                                  {subjectLawsuits.map((lawsuit) => (
                                    <CollapsibleSection
                                      key={lawsuit.Number}
                                      title={`${t('common:action')}: (${lawsuit.Number})`}
                                    >
                                      <LawsuitContent lawsuit={lawsuit} />
                                    </CollapsibleSection>
                                  ))}
                                </CollapsibleSection>
                              ))}
                            </CollapsibleSection>
                          )
                        })
                      })()}
                    </>
                  ) : (
                    <Typography>{t('common:lawsuits.NoLawsuitsFound')}</Typography>
                  )}
                </CollapsibleSection>
              )
            })}
        </CollapsibleSection>
      </Grid>
    </Grid>
  )
}

export default LawsuitsOwnersCheck
